import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopCompanyProfilePage.css';
import { Trans, useTranslation } from 'react-i18next';
import { Skeleton } from '../../effects';
import { useAuthContext } from '../../../context/AuthContext';
import { useCapabilities } from '../../../capabilities/CapabilityContext';
import useApi from '../../../hooks/useApi';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import iconInstagram from '../../../assets/images/icons/icon-instagram.svg';
import iconStrategy from '../../../assets/images/icons/icon-ai-dna.svg';
import settingsIcon from '../../../assets/images/icons/icon-settings.svg';
import iconLogout from '../../../assets/images/icons/icon-logout.svg';

const DesktopCompanyProfileContent = ({ onNavigate }) => {
  const { logout: authLogout } = useAuthContext();
  const { getNavigationStateForHref, isAccessible } = useCapabilities();
  const { notify, confirm } = useDesktopAlert();
  const { t } = useTranslation();
  const { makeRequest } = useApi();

  const [isInstagramConnected, setIsInstagramConnected] = useState(false);
  const [instagramProfile, setInstagramProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlatform, setLoadingPlatform] = useState(null);
  const [error, setError] = useState(null);

  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const companyDnaCapability = getNavigationStateForHref('/strategizer?view=dna');
  const canOpenCompanyDna = !companyDnaCapability.managed || isAccessible(companyDnaCapability);

  const iosSteps = useMemo(() => ([
    { number: '1', mainKey: 'settings.app.ios.steps.share.main', hintKey: 'settings.app.ios.steps.share.hint' },
    { number: '2', mainKey: 'settings.app.ios.steps.addToHome.main', hintKey: 'settings.app.ios.steps.addToHome.hint' },
    { number: '3', mainKey: 'settings.app.ios.steps.confirm.main', hintKey: 'settings.app.ios.steps.confirm.hint' }
  ]), []);

  const fetchConnectedAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await makeRequest('/api/v1/social/status', {
        method: 'GET'
      });
      const instagramData = data.platforms?.instagram || { connected: false };

      setIsInstagramConnected(instagramData.connected);
      setInstagramProfile(instagramData.connected ? { name: instagramData.username } : null);
    } catch (err) {
      setError(t('settings.errors.loadInstagram', { error: err.message }));
      console.error('[DesktopCompanyProfile] Failed to load instagram status', err);
    } finally {
      setIsLoading(false);
    }
  }, [authLogout, t]);

  useEffect(() => {
    fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  useEffect(() => {
    const handleOAuthCallback = () => {
      const callbackData = sessionStorage.getItem('oauth_callback_data');
      if (callbackData) {
        try {
          const parsedData = JSON.parse(callbackData);
          sessionStorage.removeItem('oauth_callback_data');
          fetchConnectedAccounts();

          if (parsedData.success) {
            setError(null);
          } else {
            setError(t('settings.errors.oauthConnect', {
              platform: parsedData.platform,
              error: parsedData.error
            }));
          }
        } catch (err) {
          console.error('[DesktopCompanyProfile] Error parsing callback data', err);
          setError(t('settings.errors.oauthCallback'));
        }
      }

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state) {
        fetchConnectedAccounts();
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };

    handleOAuthCallback();
  }, [fetchConnectedAccounts, t]);

  useEffect(() => {
    const installed = window.matchMedia('(display-mode: standalone)').matches;
    setIsPWAInstalled(installed);

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstagramDisconnect = useCallback(async () => {
    try {
      setError(null);
      setLoadingPlatform('instagram');

      await makeRequest('/api/v1/social/disconnect/instagram', {
        method: 'DELETE',
        data: { confirm: true }
      });

      setIsInstagramConnected(false);
      setInstagramProfile(null);
    } catch (err) {
      setError(t('settings.errors.disconnectInstagram', { error: err.message }));
      console.error('[DesktopCompanyProfile] Instagram disconnect error', err);
    } finally {
      setLoadingPlatform(null);
    }
  }, [authLogout, t]);

  const handleInstagramConnect = useCallback(async () => {
    if (isInstagramConnected) {
      await handleInstagramDisconnect();
      return;
    }

    try {
      setError(null);
      setLoadingPlatform('instagram');

      const response = await makeRequest('/api/v1/social/connect/instagram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: {}
      });

      if (response.oauth_url) {
        window.location.href = response.oauth_url;
        return;
      }

      throw new Error(t('desktop.companyProfile.errors.missingOAuthUrl', { defaultValue: 'Missing OAuth URL' }));
    } catch (err) {
      console.error('[DesktopCompanyProfile] Instagram connect error', err);
      setError(t('settings.errors.connectInstagram', { error: err.message }));
    } finally {
      setLoadingPlatform(null);
    }
  }, [isInstagramConnected, handleInstagramDisconnect, makeRequest, t]);

  const handleLogout = useCallback(async () => {
    try {
      const confirmed = await confirm({
        title: t('settings.logout.confirm'),
        variant: 'warning',
        confirmText: t('settings.logout.confirmButton', { defaultValue: 'Logout' }),
        cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
      });

      if (!confirmed) {
        return;
      }

      await authLogout();
      if (onNavigate) {
        onNavigate('/desktop/login');
      }
    } catch (err) {
      console.error('[DesktopCompanyProfile] Logout error', err);
      setError(t('settings.errors.logout', { error: err.message }));
    }
  }, [authLogout, confirm, onNavigate, t]);

  const handleDNAClick = useCallback(() => {
    if (!canOpenCompanyDna) {
      return;
    }
    const targetHref = '/strategizer?view=dna';
    if (onNavigate) {
      onNavigate(targetHref);
    } else {
      notify({
        variant: 'info',
        title: t('settings.companyProfile.cardTitle'),
        description: t('settings.companyProfile.cardDescription')
      });
    }
  }, [canOpenCompanyDna, notify, onNavigate, t]);

  const handleAccountSettingsClick = useCallback(() => {
    const targetHref = '/desktop/profile/settings';
    if (onNavigate) {
      onNavigate(targetHref);
    } else if (typeof window !== 'undefined') {
      window.location.href = targetHref;
    }
  }, [onNavigate]);

  const handlePWAInstall = useCallback(async () => {
    if (isPWAInstalled) {
      notify({
        variant: 'info',
        title: t('settings.app.alerts.alreadyInstalled')
      });
      return;
    }

    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) {
      notify({
        variant: 'warning',
        title: t('settings.app.alerts.unavailable')
      });
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsPWAInstalled(true);
        notify({
          variant: 'success',
          title: t('settings.app.alerts.installed', { defaultValue: 'App installata' })
        });
      }

      setDeferredPrompt(null);
    } catch (err) {
      console.error('[DesktopCompanyProfile] PWA installation error', err);
      setError(t('settings.errors.pwaInstall', { error: err.message }));
    }
  }, [deferredPrompt, isIOS, isPWAInstalled, notify, t]);

  const isLoadingInstagram = loadingPlatform === 'instagram';
  const instagramButtonLabel = isLoadingInstagram
    ? t('common.loading')
    : isInstagramConnected
      ? t('settings.instagram.disconnect')
      : t('settings.instagram.connect');

  const renderSkeleton = () => (
    <>
      <div className="loopino-desktop-grid loopino-company-grid">
        <div className="loopino-card loopino-company-card">
          <Skeleton width="40%" height={18} shimmer style={{ marginBottom: 8 }} />
          <Skeleton width="70%" height={22} shimmer style={{ marginBottom: 16 }} />
          <Skeleton width="100%" height={64} shimmer />
        </div>
        <div className="loopino-card loopino-company-card">
          <Skeleton width="45%" height={18} shimmer style={{ marginBottom: 8 }} />
          <Skeleton width="80%" height={20} shimmer style={{ marginBottom: 16 }} />
          <Skeleton width="100%" height={72} shimmer />
        </div>
        <div className="loopino-card loopino-company-card">
          <Skeleton width="60%" height={18} shimmer style={{ marginBottom: 8 }} />
          <Skeleton width="100%" height={88} shimmer />
        </div>
      </div>
      <div className="loopino-desktop-grid loopino-company-secondary-grid">
        <div className="loopino-card loopino-company-card loopino-company-card--compact">
          <Skeleton width="50%" height={18} shimmer style={{ marginBottom: 12 }} />
          <Skeleton width="70%" height={18} shimmer style={{ marginBottom: 12 }} />
          <Skeleton width="100%" height={40} shimmer />
        </div>
        <div className="loopino-glass-panel loopino-company-card loopino-company-card--compact">
          <Skeleton width="40%" height={18} shimmer style={{ marginBottom: 12 }} />
          <Skeleton width="90%" height={36} shimmer />
        </div>
      </div>
    </>
  );

  const rawCompanyProfileTitle = t('settings.companyProfile.title', { defaultValue: 'Profilo' });
  const companyProfileTitle = t('desktop.companyProfile.title', { defaultValue: rawCompanyProfileTitle });

  if (isLoading) {
    return (
      <div className="loopino-desktop-shell loopino-company-shell">
        <header className="loopino-desktop-post-review-header loopino-company-header">
          <div>
            <p className="loopino-desktop-post-review-eyebrow">
              {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
            </p>
            <h1>{companyProfileTitle}</h1>
            <p className="loopino-desktop-post-review-summary">
              {t('settings.companyProfile.cardDescription', {
                defaultValue: 'Gestisci le connessioni social, il DNA aziendale e le impostazioni account dalla nuova interfaccia desktop.'
              })}
            </p>
          </div>
        </header>
        {renderSkeleton()}
      </div>
    );
  }

  return (
    <div className="loopino-desktop-shell loopino-company-shell">
      <header className="loopino-desktop-post-review-header loopino-company-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{companyProfileTitle}</h1>
          <p className="loopino-desktop-post-review-summary">
            {t('settings.companyProfile.cardDescription', {
              defaultValue: 'Gestisci le connessioni social, il DNA aziendale e le impostazioni account dalla nuova interfaccia desktop.'
            })}
          </p>
        </div>
      </header>

      {error && (
        <div className="loopino-company-alert error">
          {error}
        </div>
      )}

      <div className="loopino-desktop-grid loopino-company-grid">
        <section className="loopino-card loopino-company-card">
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.instagram.title', { defaultValue: 'Connessioni social' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.instagram.platform', { defaultValue: 'Instagram' })}
              </h2>
              <p className="loopino-card-description">
                {t('settings.instagram.description', {
                  defaultValue: 'Collega o disconnetti il profilo Instagram per pubblicare contenuti.'
                })}
              </p>
            </div>
            <div className={`loopino-company-status ${isInstagramConnected ? 'success' : ''}`}>
              {isInstagramConnected
                ? t('settings.instagram.connected', { defaultValue: 'Connesso' })
                : t('settings.instagram.connect')}
            </div>
          </div>
          <div className="loopino-company-connection">
            <div className="loopino-company-connection-info">
              <div className="loopino-company-connection-icon">
                <img src={iconInstagram} alt="" />
              </div>
              <div>
                <p className="loopino-company-connection-title">
                  {t('settings.instagram.platform', { defaultValue: 'Instagram' })}
                </p>
                <p className="loopino-company-connection-meta">
                  {isInstagramConnected && instagramProfile
                    ? t('settings.instagram.connectedAs', { name: instagramProfile.name })
                    : t('settings.instagram.connect')}
                </p>
              </div>
            </div>
            <button
              type="button"
              className={`loopino-button ${isInstagramConnected ? 'is-connected' : ''}`}
              data-variant={isInstagramConnected ? 'surface' : 'primary'}
              onClick={handleInstagramConnect}
              disabled={isLoadingInstagram}
            >
              {instagramButtonLabel}
            </button>
          </div>
        </section>

	        <section
            className={`loopino-card loopino-company-card${canOpenCompanyDna ? ' loopino-company-card--clickable' : ''}`}
            onClick={canOpenCompanyDna ? handleDNAClick : undefined}
          >
	          <div className="loopino-card-header">
	            <div className="loopino-stack" data-gap="sm">
	              <p className="loopino-eyebrow">{companyProfileTitle}</p>
              <h2 className="loopino-card-title">{t('settings.companyProfile.cardTitle')}</h2>
              <p className="loopino-card-description">
                {t('settings.companyProfile.cardDescription')}
              </p>
            </div>
            <div className="loopino-company-dna-icon">
              <img src={iconStrategy} alt="" />
            </div>
	          </div>
	          <div className="loopino-company-dna-footer">
	            <div>
	              <p className="loopino-company-dna-label">{companyProfileTitle}</p>
	              <p className="loopino-company-connection-meta">
	                {t('settings.companyProfile.cardDescription')}
	              </p>
	            </div>
	            <span className="loopino-company-arrow" aria-hidden="true">→</span>
	          </div>
	        </section>
	      </div>

      <div className="loopino-desktop-grid loopino-company-secondary-grid">
        <section
          className="loopino-card loopino-company-card loopino-company-card--compact loopino-company-card--clickable"
          onClick={handleAccountSettingsClick}
        >
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.account.title', { defaultValue: 'Account' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.account.title', { defaultValue: 'Impostazioni account' })}
              </h2>
              <p className="loopino-card-description">
                {t('settings.account.description', {
                  defaultValue: 'Aggiorna email, password e preferenze utente.'
                })}
              </p>
            </div>
            <div className="loopino-company-connection-icon ghost">
              <img src={settingsIcon} alt="" />
            </div>
          </div>
          <div className="loopino-company-dna-footer">
            <div>
              <p className="loopino-company-dna-label">
                {t('settings.account.meta', { defaultValue: 'Account' })}
              </p>
              <p className="loopino-company-connection-meta">
                {t('settings.account.meta', { defaultValue: 'Accesso rapido alle impostazioni profilo' })}
              </p>
            </div>
            <span className="loopino-company-arrow" aria-hidden="true">→</span>
          </div>
        </section>

        <section className="loopino-card loopino-company-card loopino-company-card--compact">
          <div className="loopino-card-header">
            <div className="loopino-stack" data-gap="sm">
              <p className="loopino-eyebrow">{t('settings.app.title', { defaultValue: 'App' })}</p>
              <h2 className="loopino-card-title">
                {t('settings.app.installTitle', { defaultValue: 'Installa l\'app' })}
              </h2>
              <p className="loopino-card-description">
                {isPWAInstalled
                  ? t('settings.app.installedDescription', { defaultValue: 'Installata come PWA.' })
                  : t('settings.app.installDescription', {
                    defaultValue: 'Installa la PWA di Loopino per averla sempre disponibile.'
                  })}
              </p>
            </div>
          </div>
          <div className="loopino-company-connection">
              <div className="loopino-company-connection-info">
              <div className="loopino-company-connection-icon ghost">
                <img src="/icon-512.png.svg" alt="" aria-hidden="true" />
              </div>
              <div>
                <p className="loopino-company-connection-title">
                  {t('settings.app.title', { defaultValue: 'App' })}
                </p>
                <p className="loopino-company-connection-meta">
                  {isIOS
                    ? t('settings.app.ios.title', { defaultValue: 'Guida installazione iOS' })
                    : t('settings.app.installDescription', {
                      defaultValue: 'Installa la PWA di Loopino per averla sempre disponibile.'
                    })}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="loopino-button"
              data-variant={isPWAInstalled ? 'surface' : 'primary'}
              onClick={handlePWAInstall}
            >
              {isPWAInstalled
                ? t('settings.app.actions.installed', { defaultValue: 'Installata' })
                : (isIOS ? t('settings.app.actions.guide') : t('settings.app.actions.install'))}
            </button>
          </div>
        </section>
      </div>

      <div className="loopino-company-logout-row">
        <section className="loopino-glass-panel loopino-company-card loopino-company-logout loopino-company-card--compact">
          <div className="loopino-company-logout-info">
            <div className="loopino-company-connection-icon ghost">
              <img src={iconLogout} alt="" />
            </div>
            <div className="loopino-stack" data-gap="3xs">
              <p className="loopino-company-connection-title">{t('settings.logout.title')}</p>
              <p className="loopino-company-connection-meta">
                {t('settings.logout.description')}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="loopino-button"
            data-variant="surface"
            onClick={handleLogout}
          >
            {t('settings.logout.confirmButton', { defaultValue: 'Logout' })}
          </button>
        </section>
      </div>

      {showIOSGuide && (
        <div className="loopino-company-ios-overlay" onClick={() => setShowIOSGuide(false)}>
          <div className="loopino-company-ios-modal" onClick={(e) => e.stopPropagation()}>
            <button className="loopino-company-ios-close" onClick={() => setShowIOSGuide(false)}>
              ✕
            </button>

            <h2>{t('settings.app.ios.title')}</h2>

            <div className="loopino-company-ios-steps">
              {iosSteps.map((step) => (
                <div className="loopino-company-ios-step" key={step.number}>
                  <div className="loopino-company-ios-step-number">{step.number}</div>
                  <div className="loopino-company-ios-step-content">
                    <p>
                      <Trans i18nKey={step.mainKey} components={{ strong: <strong /> }} />
                    </p>
                    <p className="loopino-company-ios-step-hint">{t(step.hintKey)}</p>
                  </div>
                </div>
              ))}
            </div>

            <button className="loopino-button" data-variant="primary" onClick={() => setShowIOSGuide(false)}>
              {t('settings.app.ios.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DesktopCompanyProfilePage = (props) => (
  <DesktopAlertProvider>
    <DesktopCompanyProfileContent {...props} />
  </DesktopAlertProvider>
);

export default DesktopCompanyProfilePage;
